import React, { ClassAttributes, IframeHTMLAttributes } from 'react';
import usePortal from 'react-cool-portal';
import { Box, BoxProps, Button, Spinner } from '@apia/theme';
import { customEvents } from '../../../../utils/dom';
import useModalManager from '../modals/iframeModal/useModalManager';
import Overlay from '../modals/Overlay';

export type TIframeModal = Omit<
  ClassAttributes<HTMLIFrameElement> &
    IframeHTMLAttributes<HTMLIFrameElement> &
    BoxProps,
  'ref'
> & { onClose?: () => void } & Record<string, unknown>;

const IFrameOverlay = ({ className, onClose, ...props }: TIframeModal) => {
  const { Portal } = usePortal({
    containerId: 'AppModals',
    autoRemoveContainer: false,
    escToHide: false,
    clickOutsideToHide: false,
    defaultShow: true,
    internalShowHide: false,
  });

  const { isLoading, ref, setRef } = useModalManager({
    onClose,
  });

  React.useEffect(() => {
    const listenForClose = () => {
      if (onClose) onClose();
    };
    ref?.addEventListener(customEvents.iframeModalClose, listenForClose);
    return () => {
      ref?.removeEventListener(customEvents.iframeModalClose, listenForClose);
    };
  }, [onClose, ref]);

  return (
    <Portal>
      <Overlay
        isOpen
        onOverlayClicked={() => {}}
        onExited={() => {}}
        shouldAvoidInitialFocusTrap
        {...window.getVariant('layout.common.modals.iframeOverlay')}
      >
        <Button
          type="button"
          sx={{
            opacity: 0,
            position: 'absolute',
            top: '-50px',
          }}
        >
          {window.WAIT_A_SECOND}
        </Button>
        {isLoading && <Spinner />}
        <Box
          as="iframe"
          {...props}
          ref={setRef}
          className={`${className ?? ''} jspModal`}
        />
      </Overlay>
    </Portal>
  );
};

export default React.memo(IFrameOverlay);
